import { Paper, Stack, Text, Group, Badge } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { PhysicalGrowthData } from './PhysicalGrowthInput';

// -----------------------------------------------------------------------------
export interface PhysicalGrowthPreviousProps {
  data: PhysicalGrowthData | null;
  encounterDate?: string;
}

// -----------------------------------------------------------------------------
export const PhysicalGrowthPrevious = ({
  data,
  encounterDate,
}: PhysicalGrowthPreviousProps) => {
  const { t } = useTranslation(['patient-pages']);

  // Check if there's any data to show
  const hasData =
    data &&
    (data.value ||
      data.comments ||
      data.weight ||
      data.height ||
      data.headCircumference);

  const formatValue = (value: PhysicalGrowthData['value']): string => {
    if (value === null) {
      return '-';
    }

    return value === 'within-normal-limits'
      ? t('physicalGrowth.withinNormalLimits', {
          ns: 'patient-pages',
          defaultValue: 'Within normal limits',
        })
      : t('physicalGrowth.pathological', {
          ns: 'patient-pages',
          defaultValue: 'Pathological',
        });
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) {
      return '';
    }

    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  return (
    <Paper p="md" withBorder radius="md" bg="blue.0" w={300}>
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Text fw={600} size="sm">
            {t('psychomotorDevelopment.previousEncounter', {
              ns: 'patient-pages',
              defaultValue: 'Previous Encounter',
            })}
          </Text>
          {encounterDate && (
            <Badge size="xs" variant="light" color="blue">
              {formatDate(encounterDate)}
            </Badge>
          )}
        </Group>

        {!hasData ? (
          <Text size="xs" c="dimmed" ta="center" py="sm">
            {t('psychomotorDevelopment.noPreviousData', {
              ns: 'patient-pages',
              defaultValue:
                'There are no data from the previous clinical examination',
            })}
          </Text>
        ) : (
          <Stack gap="xs">
            {/* Status */}
            {data.value && (
              <Stack gap={2}>
                <Text size="xs" fw={500} c="dimmed">
                  {t('physicalGrowth.title', {
                    ns: 'patient-pages',
                    defaultValue: 'Physical Growth',
                  })}
                  {': '}
                  <Badge
                    size="xs"
                    variant="light"
                    color={data.value === 'pathological' ? 'red' : 'green'}
                  >
                    {formatValue(data.value)}
                  </Badge>
                </Text>
              </Stack>
            )}

            {/* Measurements */}
            {(data.weight || data.height || data.headCircumference) && (
              <Stack gap={2}>
                {data.weight && (
                  <Text size="xs" c="dimmed">
                    {t('examination.content.vitals.weight', {
                      ns: 'patient-pages',
                      defaultValue: 'Weight',
                    })}
                    {': '}
                    {data.weight} kg
                  </Text>
                )}
                {data.height && (
                  <Text size="xs" c="dimmed">
                    {t('examination.content.vitals.height', {
                      ns: 'patient-pages',
                      defaultValue: 'Height',
                    })}
                    {': '}
                    {data.height} cm
                  </Text>
                )}
                {data.headCircumference && (
                  <Text size="xs" c="dimmed">
                    {t('examination.content.vitals.headCircumference', {
                      ns: 'patient-pages',
                      defaultValue: 'Head Circumference',
                    })}
                    {': '}
                    {data.headCircumference} cm
                  </Text>
                )}
              </Stack>
            )}

            {/* Comments */}
            {data.comments && (
              <Stack gap={2}>
                <Text size="xs" fw={500} c="dimmed">
                  {t('physicalGrowth.comments', {
                    ns: 'patient-pages',
                    defaultValue: 'Comments',
                  })}
                </Text>
                <Text size="xs" c="dimmed" lineClamp={3}>
                  {data.comments}
                </Text>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};
